import { BaseModel } from './baseModel';
import { ProfileSettings } from './profileSettings';
import type { Agent } from './agent';

// Profile status enum
export enum ProfileStatus {
  Active = 'active',
  Pending = 'pending',
  Deleted = 'deleted',
  Suspended = 'suspended',
}

export interface ProfileMetadata {
  settings: ProfileSettings;
  doNotShow?: {
    walkthrough?: boolean;
  };
  slackActivity?: boolean;
}

export interface UserPlan extends BaseModel {
  seatPriceMonthly?: number;
  seatPriceAnnual?: number;
  trial: UserTrial[];
}

export interface UserTrial {
  status: 'active' | 'expired';
  certificationId: string;
  expires: string;
}

export interface Profile extends Omit<BaseModel, 'metadata'> {
  metadata: ProfileMetadata;
  status: ProfileStatus;
  userId: string;
  extUserId: string | null;
  default: boolean;
  persona: string;
  roles: string[];
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  initials: string | null;
  avatarUrl: string | null;
  timezone: string;
  invitedById: string;
  invitationAcceptedAt: string | null;
  invitationRejectedAt: string | null;
  hubSpotId: string | null;
  businessId: string;
  lastSeenUpdate: string | null;
  lastAuthentication: string | null;
  invitationAck: boolean;
  userPlan?: UserPlan;
  invitedBy?: Profile;
  business?: { name: string; demo?: boolean };
  agent?: Agent;
}

// Simplified profile for invitation context
export interface InvitedByProfile {
  profileId: string;
  displayName: string;
  avatarUrl: string | null;
  userId: string;
  persona: string;
}

export interface ProfileByEmailResponse {
  id: string;
  email: string;
  displayName: string;
  businessId: string;
  business: {
    clientId: string;
    client: { name: string };
    activePlan: {
      totalSeats: number;
      mysteryShopPlan: string | null;
      shopsMonthly: number | null;
      agentId: string | null;
      stripeRedirectLink?: string;
    };
    demo: boolean;
  };
}

// Helper function to check if profile is a team owner
export function isTeamOwner(profile: Profile | null | undefined): boolean {
  return !!(profile && profile.roles?.includes('owner'));
}

// Team/client owners cannot be suspended or removed via admin actions
export function isProtectedBusinessOwner(roles?: string[]): boolean {
  return !!(roles?.includes('owner') || roles?.includes('client_owner'));
}

// Helper function to check if profile is a team admin
export function isTeamAdmin(profile: Profile | null | undefined): boolean {
  return !!(profile && profile.roles?.includes('admin'));
}

// Helper function to check if profile is a company shop super admin
export function isCompanyShopSuperAdmin(profile: Profile | null | undefined): boolean {
  return !!(profile && (profile.roles?.includes('company_shop_super_admin') || profile.roles?.includes('devops_admin')));
}

// Helper function to check if profile is a company shop admin (any level)
export function isCompanyShopAdmin(profile: Profile | null | undefined): boolean {
  return !!(profile && (profile.roles?.includes('company_shop_admin') || profile.roles?.includes('company_shop_super_admin') || profile.roles?.includes('devops_admin')));
}

// Helper function to check if profile is an account admin
export function isAccountAdmin(profile: Profile | null | undefined): boolean {
  return !!(profile && (
    profile.roles?.includes('devops_admin')
    || profile.roles?.includes('account_admin')
    || profile.roles?.includes('account_super_admin')
  ));
}

// Suspend/activate/remove business profiles from Rev Admin (cross-business)
export function canManageBusinessProfiles(profile: Profile | null | undefined): boolean {
  return !!(profile && (
    profile.roles?.includes('admin_app')
    || profile.roles?.includes('account_admin')
    || profile.roles?.includes('devops_admin')
  ));
}
// Helper function to check if profile is a content admin (admin_app role)
export function isContentAdmin(profile: Profile | null | undefined): boolean {
  return !!(profile && (profile.roles?.includes('admin_app') || profile.roles?.includes('devops_admin')));
}
